import { Transform, Type } from 'class-transformer'
import { IsBoolean, IsIn, IsInt, IsOptional, IsPositive, Max, Min } from 'class-validator'
import 'reflect-metadata'
import { V2ProductDto } from './v2-product'

/**
 * How close a product is to the product of the details panel (AO-11837): the same ingredients at the same
 * concentration and unit, the same ingredients at another concentration, or only some of them.
 */
export type V2ClosestProductSimilarity = 'identical' | 'same-ingredients' | 'partial'
export const V2_CLOSEST_PRODUCT_SIMILARITIES: V2ClosestProductSimilarity[] = [
  'identical',
  'same-ingredients',
  'partial',
]

/** Closest products the panel shows at first in each level, and adds at each "Show more" */
export const V2_CLOSEST_PRODUCTS_PAGE_SIZE = 8
/** Most closest products one request returns, "Show all" included */
export const V2_CLOSEST_PRODUCTS_MAX_LIMIT = 500

export interface V2ClosestProduct {
  product: V2ProductDto
  similarity: V2ClosestProductSimilarity
  /** Ingredients it shares with the product of the panel */
  sharedCount: number
  /** Linked to the product of the panel as reference product in a country, in either direction */
  isReference: boolean
  /** Specifications holding it, among those of the caller's regions */
  specificationIds: number[]
}

/** The closest products of one similarity level: their counts and a slice of them, closest first. */
export interface V2ClosestProductLevel {
  similarity: V2ClosestProductSimilarity
  count: number
  /** Those held by the checked specification, when one is checked */
  authorisedCount?: number
  /** Those matching the query: all of them, or those of the checked specification */
  matchCount: number
  items: V2ClosestProduct[]
}

export interface V2ClosestProductsPage {
  /** Every closest product */
  total: number
  /** Levels holding at least one closest product, closest level first; the queried one alone when there is one */
  levels: V2ClosestProductLevel[]
}

export class V2ClosestProductQuery {
  /** Specification the closest products are checked against */
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @IsPositive()
  specificationId?: number

  /** Only the closest products of the checked specification; ignored without one */
  @IsOptional()
  @Transform(({ value }) => value === true || value === 'true')
  @IsBoolean()
  authorisedOnly = false

  /** Only this level, for its "Show more" */
  @IsOptional()
  @IsIn(V2_CLOSEST_PRODUCT_SIMILARITIES)
  similarity?: V2ClosestProductSimilarity

  /** First closest product of each level to return */
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(0)
  offset = 0

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(V2_CLOSEST_PRODUCTS_MAX_LIMIT)
  /** Closest products of each level to return */
  limit = V2_CLOSEST_PRODUCTS_PAGE_SIZE
}

/** A specification holding the product, with the limits it sets for it. */
export interface V2ProductSpecificationUse {
  id: number
  specificationId: number
  specificationName: string
  regionId: number
  regionName: string
  cropTypeName?: string
  dosage: number
  rateUnit?: string
  passageNumber?: number
  phi?: number
}

/** The product details panel, closest products aside (they come page by page). */
export interface V2ProductDetails {
  product: V2ProductDto
  /** Country id → id of the reference product, when it is another product of the referential */
  referenceProductIds: Record<number, number>
  /** Specifications holding the product, among those of the caller's regions */
  specifications: V2ProductSpecificationUse[]
}
